import { useEffect, useState } from "react";
import { DivideCircle, Info, Presentation, RefreshCcw, X } from "lucide-react";
import { Slide, SlideColumn, SlideStructure } from "@/api/models";

interface SlideStructurePreviewProps {
  slideData: SlideStructure;
  isRegenerating: boolean;
  createAiImages: boolean;
  onSetCreateAiImages: (value: boolean) => void;
  onRegenerate: (changeRequest: string) => void;
  onConfirm: (slideData: SlideStructure) => void;
  onDismiss: () => void;
}

type SlideLayout = "table" | "columns" | "standard";

function detectSlideLayout(slide: Slide): SlideLayout {
  if (slide.table) return "table";

  const bullets = slide.bullet_points;
  if (bullets && bullets.length > 0) {
    const first = bullets[0];
    if (typeof first === "object" && !Array.isArray(first) && "heading" in first) return "columns";
  }

  return "standard";
}

function renderStandardSlide(slide: Slide) {
  return (
    <ul className="list-disc list-inside space-y-1 text-sm dark:text-white">
      {(slide.bullet_points || []).map((item, i) => {
        if (Array.isArray(item)) {
          return (
            <ul key={i} className="ml-5 list-[circle] list-inside space-y-0.5">
              {item.map((sub, j) => (
                <li key={j} className="text-sm dark:text-white">{sub}</li>
              ))}
            </ul>
          );
        }
        if (typeof item === "string") {
          return <li key={i}>{item}</li>;
        }
        return null;
      })}
    </ul>
  );
}

function renderColumnsSlide(slide: Slide) {
  const cols = (slide.bullet_points || []).filter(
    (item): item is SlideColumn =>
      typeof item === "object" && !Array.isArray(item) && "heading" in item
  );
  const colClass = cols.length === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div className={`grid ${colClass} gap-4`}>
      {cols.map((col, i) => (
        <div key={i}>
          <h4 className="text-xs font-semibold uppercase tracking-wide mb-1 dark:text-white">{col.heading}</h4>
          <ul className="list-disc list-inside space-y-1">
            {col.bullet_points.map((bp, j) => (
              <li key={j} className="text-sm dark:text-white">{bp}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function renderTableSlide(slide: Slide) {
  if (!slide.table) return null;
  return (
    <div className="overflow-x-auto">
      <table className="table table-sm w-full">
        <thead>
          <tr>
            {slide.table.headers.map((h, i) => (
              <th key={i} className="text-xs dark:text-white">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slide.table.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="text-sm dark:text-white">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SlideStructurePreview({
  slideData,
  isRegenerating,
  createAiImages,
  onSetCreateAiImages,
  onRegenerate,
  onConfirm,
  onDismiss,
}: SlideStructurePreviewProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [changeRequest, setChangeRequest] = useState("");

  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [slideData]);

  const total = slideData.slides.length;
  const safeIndex = Math.min(currentSlideIndex, total - 1);
  const currentSlide = slideData.slides[safeIndex];
  const layout = currentSlide ? detectSlideLayout(currentSlide) : "standard";

  if (!currentSlide) return null;

  return (
    <section className="mx-auto max-w-[1024px] w-full mt-4 mb-3 px-2" aria-label="Presentation outline preview">
      <div className="rounded-3xl border border-base-300 bg-base-100 shadow-short p-4 md:p-6">
        <header className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary mb-1">Presentation outline</p>
            <h2 className="text-xl font-bold dark:text-white truncate">{slideData.title}</h2>
            {slideData.subtitle && <p className="text-sm text-base-content/65 mt-1">{slideData.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:inline text-sm text-base-content/60">{total} slides</span>
            <button type="button" onClick={onDismiss} className="btn btn-ghost btn-sm btn-square" aria-label="Dismiss presentation preview"><X size={17} /></button>
          </div>
        </header>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-5 scrollbar-thin" role="tablist" aria-label="Slides">
          {slideData.slides.map((slide, index) => (
            <button
              key={`${slide.heading}-${index}`}
              type="button"
              role="tab"
              aria-selected={index === safeIndex}
              onClick={() => setCurrentSlideIndex(index)}
              className={`min-w-32 max-w-44 rounded-xl border px-3 py-2 text-left transition-colors ${index === safeIndex ? "border-primary bg-primary text-primary-content" : "border-base-300 bg-base-200/60 hover:border-primary/50"}`}
            >
              <span className="block text-[11px] font-semibold opacity-75">SLIDE {index + 1}</span>
              <span className="block text-sm font-medium truncate mt-0.5">{slide.heading || "Untitled slide"}</span>
            </button>
          ))}
        </div>

        <article className="rounded-2xl border border-base-300 bg-neutral/45 p-5 md:p-7 min-h-[300px]">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-base-content/55 mb-5">Slide {safeIndex + 1} of {total}</p>
          <h3 className="text-xl font-semibold dark:text-white leading-snug">{currentSlide.heading}</h3>
          {currentSlide.subtitle && <p className="text-sm text-base-content/65 mt-1 mb-5">{currentSlide.subtitle}</p>}
          <div className="mt-6">
            {layout === "table" && renderTableSlide(currentSlide)}
            {layout === "columns" && renderColumnsSlide(currentSlide)}
            {layout === "standard" && renderStandardSlide(currentSlide)}
          </div>
        </article>

        <div className="rounded-2xl bg-base-200/70 p-4 my-2">
          <h3 className="text-sm font-semibold">Review this outline</h3>
          <p className="text-xs text-base-content/65 mt-1 mb-4">Describe the changes you want before the deck is created.</p>
          <label className="text-xs font-medium mb-1.5 block" htmlFor="slide-preview-changes">Changes (optional)</label>
          <textarea
            id="slide-preview-changes"
            className="textarea textarea-bordered w-full rounded-xl text-sm bg-base-100 dark:text-white"
            rows={2}
            placeholder="e.g. Add a market trends slide, simplify the table…"
            value={changeRequest}
            onChange={e => setChangeRequest(e.target.value)}
            disabled={isRegenerating}
          />
          <label className="flex items-start gap-3 mt-2 cursor-pointer">
            <input type="checkbox" className="checkbox checkbox-sm checkbox-primary mt-0.5" checked={createAiImages} onChange={e => onSetCreateAiImages(e.target.checked)} disabled={isRegenerating} />
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium mt-0.5">Generate AI images</span>
              <span className="flex gap-1.5 text-xs text-base-content/65 mt-1"><Info size={13} className="shrink-0" />{createAiImages ? "Adds a few minutes to creation." : "Uses relevant SMS imagery when available."}</span>
            </div>
          </label>
        </div>

        <footer className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button type="button" className="btn btn-ghost" onClick={() => { onRegenerate(changeRequest); setChangeRequest(""); }} disabled={isRegenerating}>
            {isRegenerating ? <><span className="loading loading-spinner loading-xs" /> Applying Changes</> : <><RefreshCcw size={16} /> Apply Changes</>}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm(slideData)} disabled={isRegenerating}>
            <Presentation size={16} /> Create PowerPoint
          </button>
        </footer>
      </div>
    </section>
  );
}
